import styled, { css } from 'styled-components';

import {
  ExampleQuestionsContainer,
  FilterListContainer,
} from '@/components/organism/ExampleQuestionsList/ExampleQuestionsList.styles';

const CONTAINER_PADDING_X = '40px';

export const InputContainer = styled.div<{ $disabled: boolean; $warning: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr auto;
  column-gap: 8px;
  height: 87px;
  border: 1.5px solid var(--gray-light);
  border-radius: 999px;
  padding: 8px ${CONTAINER_PADDING_X};
  margin-top: 1rem;
  background: var(--color-white);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  //INFO: The focus ring follows real DOM focus instead of React state, so it can never outlive the
  //focus itself. It is suppressed while the composer is busy so the busy state reads unambiguously.
  //It is also suppressed at the character limit so the warning ring wins without a specificity fight.
  ${({ $warning }) =>
    !$warning &&
    css`
      &[aria-busy='false']:has(textarea:focus) {
        border-color: var(--brand-primary);
        box-shadow: 0 0 0 3px rgba(0, 171, 169, 0.1);
      }
    `}

  ${({ $warning }) =>
    $warning &&
    css`
      border-color: var(--brand-warning);
      box-shadow: 0 0 0 3px rgba(255, 142, 60, 0.15);
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      background: var(--gray-lightest);
      cursor: not-allowed;
      pointer-events: none;

      //INFO: The textarea stays clickable while busy so a mouse user can keep or regain the caret
      //without being able to type — the composer is read-only, not disabled.
      textarea {
        pointer-events: auto;
      }
    `}
`;

export const StyledInputWrapper = styled.div`
  position: relative;
  grid-column: 1;
  grid-row: 1;
  display: flex;
  min-height: 0;
`;

export const StyledInput = styled.textarea`
  width: 100%;
  flex: 1;
  min-height: 0;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.5rem;
  overflow-y: auto;
  background: transparent;
  color: var(--color-black);
  caret-color: var(--color-black);
  padding: 4px 8px 4px 0;
  box-sizing: border-box;
  z-index: 2;
  scrollbar-color: var(--gray-lighter) var(--color-white);

  &::placeholder {
    color: var(--gray);
    opacity: 1;
  }

  &:read-only {
    cursor: not-allowed;
  }
`;

export const SlotFadeWrapper = styled.div<{ $hidden: boolean }>`
  grid-column: 1;
  grid-row: 2;
  display: flex;
  align-items: center;
  overflow: hidden;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  max-height: ${({ $hidden }) => ($hidden ? '0px' : '34px')};
  margin-top: ${({ $hidden }) => ($hidden ? '0px' : '8px')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transition:
    opacity 300ms ease,
    max-height 300ms ease,
    margin-top 300ms ease;

  & > ${ExampleQuestionsContainer} {
    margin-top: 0;
  }

  ${FilterListContainer} {
    position: absolute;
    bottom: calc(100% + 16px);
    left: ${CONTAINER_PADDING_X};
    right: ${CONTAINER_PADDING_X};
    z-index: 10;
    max-height: 60vh;
    overflow-y: auto;
    background: var(--color-white);
    border: 1px solid var(--gray-light);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    padding: 12px;
  }
`;

export const ControlsColumn = styled.div`
  grid-column: 2;
  grid-row: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  margin-right: 5px;
`;

export const SendButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: none;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ $active }) => ($active ? 'var(--brand-primary)' : 'var(--gray-lighter)')};
  //INFO: The inactive label uses --gray-darker to clear the 4.5:1 contrast minimum: the button is
  //aria-disabled rather than disabled, so it stays an active control and WCAG 1.4.3 applies to it.
  color: ${({ $active }) => ($active ? '#fff' : 'var(--gray-darker)')};

  &:hover:not([aria-disabled='true']) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 171, 169, 0.25);
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }

  .rioglyph-send {
    font-size: 16px;
  }
`;

export const SuggestionOverlay = styled.span<{ caretLeft: number; caretTop: number }>`
  position: absolute;
  pointer-events: none;
  white-space: pre;
  overflow: hidden;
  box-sizing: border-box;
  color: var(--gray);
  left: ${({ caretLeft }) => caretLeft}px;
  top: ${({ caretTop }) => caretTop}px;
`;

export const ErrorMessage = styled.div`
  color: red;
  margin-top: 4px;
  font-size: 14px;
`;

export const WarningMessage = styled.div`
  color: var(--gray);
  margin-top: 4px;
  font-size: 14px;
  padding-bottom: 4px;
  padding-right: ${CONTAINER_PADDING_X};
  display: flex;
  align-items: flex-end;
  justify-self: flex-end;
`;

export const ImgSrc = styled.span`
  font-size: 16px;
  position: relative;
  bottom: 0.2rem;
`;
